import * as geofire from 'geofire-common';

/**
 * GeoUtils - Efficient spatial operations for Firebase
 * 
 * Uses geohash-based indexing for fast location queries without scanning entire database.
 * Based on Firebase's geofire-common library.
 */
class GeoUtils {
    // Earth's radius in miles and kilometers
    private static readonly EARTH_RADIUS_MILES = 3959;
    private static readonly EARTH_RADIUS_KM = 6371;

    /**
     * Generate a geohash for given coordinates
     * This allows Firebase to index locations hierarchically
     */
    static generateGeohash(lat: number, lng: number): string {
        return geofire.geohashForLocation([lat, lng]);
    }

    /**
     * Calculate distance between two points using Haversine formula
     * Returns distance in miles or kilometers
     */
    static calculateDistance(
        lat1: number,
        lng1: number,
        lat2: number,
        lng2: number,
        inMiles: boolean = true
    ): number {
        const radius = inMiles ? this.EARTH_RADIUS_MILES : this.EARTH_RADIUS_KM;
        
        const dLat = this.toRadians(lat2 - lat1);
        const dLng = this.toRadians(lng2 - lng1);
        
        const a = 
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
        
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        
        return radius * c;
    }

    /**
     * Get query bounds for a location search
     * Returns array of [start, end] geohash ranges to query
     * This is the key optimization - only query relevant geohash ranges!
     */
    static getGeohashQueryBounds(
        lat: number,
        lng: number,
        radiusInMiles: number
    ): string[][] {
        const radiusInKm = radiusInMiles * 1.60934; // Convert miles to km
        const center: [number, number] = [lat, lng];
        const bounds = geofire.geohashQueryBounds(center, radiusInKm * 1000); // Convert to meters
        
        return bounds;
    }

    /**
     * Check if a location is within the search radius
     * Used for final filtering after geohash query
     */
    static isWithinRadius(
        centerLat: number,
        centerLng: number,
        pointLat: number,
        pointLng: number,
        radiusInMiles: number
    ): boolean {
        const distance = this.calculateDistance(
            centerLat,
            centerLng,
            pointLat,
            pointLng,
            true
        );
        
        return distance <= radiusInMiles;
    }

    /**
     * Helper to convert degrees to radians
     */
    private static toRadians(degrees: number): number {
        return degrees * (Math.PI / 180);
    }

    /**
     * Create location object for Firestore storage
     * Stores both coordinates and geohash for efficient querying
     */
    static createLocationObject(lat: number, lng: number) {
        return {
            lat,
            lng,
            geohash: this.generateGeohash(lat, lng),
        };
    }

    /**
     * Get bounding box for a location (useful for map viewport queries)
     * Returns { north, south, east, west } bounds
     */
    static getBoundingBox(
        lat: number,
        lng: number,
        radiusInMiles: number
    ): { north: number; south: number; east: number; west: number } {
        const radiusInKm = radiusInMiles * 1.60934;
        const latChange = radiusInKm / 111.32; // 1 degree lat ≈ 111.32 km
        const lngChange = radiusInKm / (111.32 * Math.cos(this.toRadians(lat)));

        return {
            north: lat + latChange,
            south: lat - latChange,
            east: lng + lngChange,
            west: lng - lngChange,
        };
    }

    /**
     * Validate coordinates
     */
    static isValidCoordinates(lat: number, lng: number): boolean {
        return (
            typeof lat === 'number' &&
            typeof lng === 'number' &&
            lat >= -90 &&
            lat <= 90 &&
            lng >= -180 &&
            lng <= 180 &&
            !isNaN(lat) &&
            !isNaN(lng)
        );
    }
}

export default GeoUtils;

